// StreamTube Renderer - Creates tubular geometry for streamlines with variable radius
// Integrates with CoandaView's THREE.js rendering pipeline

class StreamTubeRenderer {
  constructor(renderingEngine) {
    this.engine = renderingEngine;
    this.tubeMeshes = [];
    this.currentColormap = 'jet';
    this.scalarRange = { min: 0, max: 1 };
  }

  /**
   * Create stream tubes from backend API response
   * @param {Object} data - API response containing streamlines data
   * @param {Object} options - Rendering options (radius, sides, colormap, etc.)
   */
  createStreamTubes(data, options = {}) {
    console.log('Creating stream tubes...', data);

    if (!data || !data.streamlines || data.streamlines.length === 0) {
      console.warn('No streamline data to render');
      return;
    }

    // Clear existing tubes
    this.clear();

    // Extract options with defaults
    const radius = options.radius || 0.01;
    const tubularSegments = options.tubularSegments || 64;
    const radialSegments = options.radialSegments || 8;
    const varyRadiusBy = options.varyRadiusBy || 'constant';
    const colormap = options.colormap || this.currentColormap;
    const scalarField = options.scalarField || 'speed';

    this.currentColormap = colormap;

    // Calculate scalar range for coloring
    this.calculateScalarRange(data.streamlines, scalarField);

    // Create a tube for each streamline
    data.streamlines.forEach((streamline, idx) => {
      const tubeMesh = this.createSingleTube(streamline, {
        radius,
        tubularSegments,
        radialSegments,
        varyRadiusBy,
        colormap,
        scalarField
      });

      if (tubeMesh) {
        this.tubeMeshes.push(tubeMesh);
        this.engine.scene.add(tubeMesh);
      }
    });

    console.log(`Created ${this.tubeMeshes.length} stream tubes`);
    return this.tubeMeshes;
  }

  /**
   * Create a single tube from streamline data
   */
  createSingleTube(streamline, options) {
    if (!streamline.points || streamline.points.length < 2) {
      console.warn('Streamline has insufficient points');
      return null;
    }

    const points = streamline.points.map(p => new THREE.Vector3(p[0], p[1], p[2]));
    const scalars = streamline[options.scalarField] || streamline.speed || [];

    // Create curve from points
    const curve = new THREE.CatmullRomCurve3(points);

    // Create tube geometry
    const geometry = this.createTubeGeometry(
      curve,
      points.length,
      options.radius,
      options.radialSegments,
      options.varyRadiusBy,
      scalars
    );

    // Apply vertex colors based on scalar field
    this.applyVertexColors(geometry, curve, scalars, options.colormap);

    // Create material
    const material = new THREE.MeshPhongMaterial({
      vertexColors: true,
      side: THREE.DoubleSide,
      shininess: 30,
      transparent: false,
      opacity: 1.0
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData.type = 'streamtube';
    mesh.userData.streamlineData = streamline;

    return mesh;
  }

  /**
   * Create tube geometry with variable radius support
   */
  createTubeGeometry(curve, tubularSegments, baseRadius, radialSegments, varyRadiusBy, scalars) {
    const geometry = new THREE.BufferGeometry();
    const vertices = [];
    const normals = [];
    const uvs = [];
    const indices = [];

    // Generate tube vertices
    const frames = this.computeFrenetFrames(curve, tubularSegments);

    for (let i = 0; i < tubularSegments; i++) {
      const t = i / (tubularSegments - 1);
      const point = curve.getPoint(t);
      const normal = frames.normals[i];
      const binormal = frames.binormals[i];

      // Calculate radius for this segment
      let radius = baseRadius;
      if (varyRadiusBy === 'speed' && scalars.length > 0) {
        const scalarIdx = Math.min(Math.floor(t * scalars.length), scalars.length - 1);
        const scalar = scalars[scalarIdx];
        // Normalize scalar to 0-1 range
        const normalizedScalar = (scalar - this.scalarRange.min) /
                                 (this.scalarRange.max - this.scalarRange.min + 1e-10);
        radius = baseRadius * (0.3 + 0.7 * normalizedScalar); // Vary between 30% and 100%
      }

      // Create circle of vertices around the curve point
      for (let j = 0; j <= radialSegments; j++) {
        const v = (j / radialSegments) * Math.PI * 2;
        const cos = Math.cos(v);
        const sin = Math.sin(v);

        // Position
        const x = point.x + radius * (cos * normal.x + sin * binormal.x);
        const y = point.y + radius * (cos * normal.y + sin * binormal.y);
        const z = point.z + radius * (cos * normal.z + sin * binormal.z);
        vertices.push(x, y, z);

        // Normal
        const nx = cos * normal.x + sin * binormal.x;
        const ny = cos * normal.y + sin * binormal.y;
        const nz = cos * normal.z + sin * binormal.z;
        normals.push(nx, ny, nz);

        // UV
        uvs.push(i / (tubularSegments - 1), j / radialSegments);
      }
    }

    // Generate indices for triangles
    for (let i = 0; i < tubularSegments - 1; i++) {
      for (let j = 0; j < radialSegments; j++) {
        const a = (radialSegments + 1) * i + j;
        const b = (radialSegments + 1) * (i + 1) + j;
        const c = (radialSegments + 1) * (i + 1) + j + 1;
        const d = (radialSegments + 1) * i + j + 1;

        indices.push(a, b, d);
        indices.push(b, c, d);
      }
    }

    geometry.setIndex(indices);
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));

    return geometry;
  }

  /**
   * Compute Frenet frames along the curve
   */
  computeFrenetFrames(curve, segments) {
    const tangents = [];
    const normals = [];
    const binormals = [];

    const normal = new THREE.Vector3();
    const vec = new THREE.Vector3();
    const theta = new THREE.Vector3();

    // Initialize first normal
    let min = Number.MAX_VALUE;
    const tx = Math.abs(curve.getTangent(0).x);
    const ty = Math.abs(curve.getTangent(0).y);
    const tz = Math.abs(curve.getTangent(0).z);

    if (tx <= min) {
      min = tx;
      normal.set(1, 0, 0);
    }
    if (ty <= min) {
      min = ty;
      normal.set(0, 1, 0);
    }
    if (tz <= min) {
      normal.set(0, 0, 1);
    }

    // Compute frames
    for (let i = 0; i < segments; i++) {
      const t = i / (segments - 1);
      const tangent = curve.getTangent(t).normalize();
      tangents.push(tangent.clone());

      if (i === 0) {
        vec.crossVectors(tangent, normal).normalize();
        normal.crossVectors(tangent, vec).normalize();
      } else {
        const prevTangent = tangents[i - 1];
        theta.crossVectors(prevTangent, tangent);

        if (theta.length() > Number.EPSILON) {
          theta.normalize();
          const angle = Math.acos(THREE.MathUtils.clamp(prevTangent.dot(tangent), -1, 1));
          normal.applyAxisAngle(theta, angle);
        }
      }

      normals.push(normal.clone());
      binormals.push(new THREE.Vector3().crossVectors(tangent, normal));
    }

    return { tangents, normals, binormals };
  }

  /**
   * Apply vertex colors based on scalar field
   */
  applyVertexColors(geometry, curve, scalars, colormapName) {
    const colors = [];
    const positions = geometry.attributes.position;
    const vertexCount = positions.count;

    // Check if centralized Colormap is available
    const hasColormap = window.Colormap && window.Colormap.getColor;

    if (!hasColormap) {
      console.warn('Colormap not found, using default colors');
      // Fallback: use uniform color
      for (let i = 0; i < vertexCount; i++) {
        colors.push(0.2, 0.6, 1.0); // Blue
      }
    } else {
      // Calculate number of tubular segments from geometry structure
      const radialSegments = this.findRadialSegments(geometry);
      const tubularSegments = Math.floor(vertexCount / (radialSegments + 1));

      // Get solid color for streamline colormap dropdown if using solid colormap
      const solidColorHex = window.Colormap.isSolidColor(colormapName)
        ? window.Colormap.getSolidColor('streamlineColormap')
        : null;

      for (let i = 0; i < tubularSegments; i++) {
        const t = i / (tubularSegments - 1);
        const scalarIdx = Math.min(Math.floor(t * scalars.length), scalars.length - 1);
        const scalar = scalars[scalarIdx] || 0;

        // Normalize scalar
        const normalizedScalar = (scalar - this.scalarRange.min) /
                                 (this.scalarRange.max - this.scalarRange.min + 1e-10);

        // Get color from centralized Colormap
        const rgb = window.Colormap.getColor(normalizedScalar, colormapName, solidColorHex);
        const r = rgb[0] / 255;
        const g = rgb[1] / 255;
        const b = rgb[2] / 255;

        // Apply same color to all vertices in this ring
        for (let j = 0; j <= radialSegments; j++) {
          colors.push(r, g, b);
        }
      }
    }

    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  }

  /**
   * Helper to find radial segments from geometry
   */
  findRadialSegments(geometry) {
    const indices = geometry.index.array;
    // First triangle uses indices a, b, d where b - a gives us radialSegments + 1
    if (indices.length >= 3) {
      return indices[1] - indices[0] - 1;
    }
    return 8; // Default fallback
  }

  /**
   * Calculate scalar range across all streamlines
   */
  calculateScalarRange(streamlines, scalarField) {
    let min = Infinity;
    let max = -Infinity;

    streamlines.forEach(streamline => {
      const scalars = streamline[scalarField] || streamline.speed || [];
      scalars.forEach(value => {
        if (value < min) min = value;
        if (value > max) max = value;
      });
    });

    this.scalarRange = {
      min: isFinite(min) ? min : 0,
      max: isFinite(max) ? max : 1
    };

    console.log(`Scalar range for ${scalarField}:`, this.scalarRange);
  }

  /**
   * Update colormap for existing tubes
   */
  updateColormap(colormapName) {
    this.currentColormap = colormapName;

    this.tubeMeshes.forEach(mesh => {
      const streamline = mesh.userData.streamlineData;
      if (!streamline) return;

      const scalars = streamline.speed || [];
      this.applyVertexColors(mesh.geometry, null, scalars, colormapName);
      mesh.geometry.attributes.color.needsUpdate = true;
    });
  }

  /**
   * Clear all tube meshes from scene
   */
  clear() {
    this.tubeMeshes.forEach(mesh => {
      if (mesh.geometry) mesh.geometry.dispose();
      if (mesh.material) mesh.material.dispose();
      this.engine.scene.remove(mesh);
    });
    this.tubeMeshes = [];
  }

  /**
   * Get all tube meshes
   */
  getTubes() {
    return this.tubeMeshes;
  }

  /**
   * Show/hide tubes
   */
  setVisible(visible) {
    this.tubeMeshes.forEach(mesh => {
      mesh.visible = visible;
    });
  }
}

// Export for use in other modules
if (typeof window !== 'undefined') {
  window.StreamTubeRenderer = StreamTubeRenderer;
}
